import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isObject from 'lodash/isObject';

// Components
import PropertyCardSwitcher from '../../../containers/PropertyCardSwitcher/PropertyCardSwitcher';
import OptionTypeSwitcher from '../OptionTypeSwitcher/OptionTypeSwitcher';
import PropertyCell from '../../PropertyCell/PropertyCell';

// Helpers
import { DEFAULT_DISPLAYED_PINS, OPTION_TYPES_COMPANY } from '../Company';
import {
  imageModifierTypes,
  language,
  localesWithEnglishAddress
} from '../../../constants';
import modifyImageLink from '../../../utils/modifyImageLink';

// styles
import * as styles from './CompanyProperties.scss';

const PROPERTIES_COUNT_TO_SHOW = {
  MOBILE: 10,
  DESKTOP: 15
};

class CompanyProperties extends Component {
  constructor(props) {
    super(props);
    const hasPropertiesForSale = !!get(this.props, [OPTION_TYPES_COMPANY.SALE, 'length']);
    const hasPropertiesForRent = !!get(this.props, [OPTION_TYPES_COMPANY.RENT, 'length']);
    const propertiesOnlyForRent = !hasPropertiesForSale && hasPropertiesForRent;

    this.state = {
      showAllProperties: false,
      propertiesInRowCount: props.isMobile ? 1 : 5,
      propertiesToShowCount: props.isMobile ? PROPERTIES_COUNT_TO_SHOW.MOBILE : PROPERTIES_COUNT_TO_SHOW.DESKTOP,
      hasPropertiesForSale,
      hasPropertiesForRent,
      displayPropertiesFor: (propertiesOnlyForRent) ? OPTION_TYPES_COMPANY.RENT : DEFAULT_DISPLAYED_PINS
    };
  }

  componentDidMount() {
    this.setPropertiesInRowCount();
    window.addEventListener('resize', this.setPropertiesInRowCount);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setPropertiesInRowCount);
  }

  getContainerRef = (ref) => { this.container = ref; };

  getPropertiesInRowCount = () => {
    if (this.props.isMobile) { return 1; }
    if (!this.container) { return 3; }

    const containerWidth = this.container.clientWidth;
    const propertyWidth = get(this.container, 'children[0].children[0].clientWidth', 230);

    const scrollWidth = 15;
    const propertyMargin = 16;

    return (Math.floor((containerWidth + scrollWidth) / (propertyWidth + propertyMargin)));
  };

  getPropertiesToShowCount = () => {
    if (this.props.isMobile) { return PROPERTIES_COUNT_TO_SHOW.MOBILE; }
    if (!this.container) { return PROPERTIES_COUNT_TO_SHOW.DESKTOP; }

    const rowsCount = 3;
    const countInRow = this.getPropertiesInRowCount();

    return countInRow * rowsCount;
  };

  setPropertiesInRowCount = () => {
    const count = this.getPropertiesInRowCount();

    if (count !== this.state.propertiesInRowCount) {
      const propertiesToShowCount = this.getPropertiesToShowCount();
      this.setState({
        propertiesInRowCount: count,
        propertiesToShowCount
      });
    }
  };

  handleShowAll = () => this.setState({ showAllProperties: true });

  handleSelectOption = (evt) => {
    evt.preventDefault();
    const { value } = evt.target;

    if (value !== this.state.displayPropertiesFor) {
      this.setState({ displayPropertiesFor: value });
    }
  };

  handlePropertyClick = (evt) => {
    const slug = get(evt, 'currentTarget.dataset.slug') || get(evt, 'currentTarget.dataset.id');
    this.props.openDetails(evt, slug);
  };

  mobilePropertyClickHandler = (preloadData) => {
    const slug = preloadData.slug || preloadData.propertyId;
    this.props.openDetails({}, slug);
  };

  renderSwitchTitle = () => (
    <OptionTypeSwitcher
      translate={this.props.translate('companyPage')}
      title={this.props.translate('companyPage', 'properties').toString()}
      activeOption={this.state.displayPropertiesFor}
      hasPropertiesForSale={this.state.hasPropertiesForSale}
      hasPropertiesForRent={this.state.hasPropertiesForRent}
      handleSelectOption={this.handleSelectOption}
    />
  );

  renderPropertyCell = (property) => {
    const englishAddressRequired = localesWithEnglishAddress.includes(this.props.locale);
    const address = englishAddressRequired
      ? property.address.en
      : (property.address[this.props.locale] || property.address[language.default]);

    const {
      id, thumbnail, price,
      property_type,
      search_option,
      additional_info: {
        rooms,
        parking,
        area: areaObject
      } = {},
      currency,
      slug
    } = property;

    const isLand = (property_type === 'land'); // temp patch
    const floor = isLand ? undefined : get(property, 'additional_info.floor.on_the');
    const roomsData = isLand ? undefined : rooms;

    const houseNumber = (address.house_number) ? ` ${address.house_number},` : '';
    const addressString = `${address.street_name} ${houseNumber}`;

    const propertyImage = (thumbnail && modifyImageLink(thumbnail, imageModifierTypes.SLIDER_THUMBNAIL)) || null;
    const area = isObject(areaObject) ? areaObject.base : areaObject;

    const commonProps = {
      neighborhood: address.neighborhood,
      key: id,
      locale: this.props.locale,
      isMobile: this.props.isMobile,
      isRtl: this.props.isRtl,
      translate: this.props.translate,
      id,
      property,
      photo: propertyImage,
      price,
      address: addressString,
      rooms: roomsData,
      parkingSpots: parking,
      city: address.city_name,
      area,
      floor,
      propertyType: this.props.translate('propertyTypes', property_type)(),
      isPromoted: property.is_promoted,
      hasVRLink: !!property.vr_link,
      searchOption: search_option,
      groundFloorTitle: this.props.translate('propertyDetails', 'details', 'groundFloorShortcut'),
      currency,
      overrideCurrency: this.props.overrideCurrency,
      slug,
      isFavorite: !!this.props.favouritePropertiesIDs.includes(id),
      toggleFavorite: () => this.props.toggleFavorite(property)
    };

    const style = {};

    if (!this.props.isMobile) {
      style.width = `calc((100% / ${this.state.propertiesInRowCount}) - 30px)`;
    }

    return (this.props.isMobile)
      ? (
        <PropertyCell
          {...commonProps}
          openDetailsPopup={this.mobilePropertyClickHandler}
        />
      )
      : (
        <PropertyCardSwitcher key={id} property={property} />
      );
  };

  renderProperties = () => {
    let properties = this.props[this.state.displayPropertiesFor] || [];

    if (!this.state.showAllProperties && properties.length > this.state.propertiesToShowCount) {
      const countToShow = this.state.propertiesToShowCount;
      properties = properties.slice(0, countToShow);
    }

    return properties.map(this.renderPropertyCell);
  };

  renderMoreButton = () => {
    const properties = this.props[this.state.displayPropertiesFor] || [];

    if (this.state.showAllProperties || properties.length <= this.state.propertiesToShowCount) {
      return null;
    }

    return (
      <div className={styles.showMoreButton}>
        <button
          onClick={this.handleShowAll}
        >
          {this.props.translate('companyPage', 'showAllProperties')} {this.props.companyName}
        </button>
      </div>
    );
  };

  render() {
    return (
      <div className={styles.propertiesContainer}>
        {this.renderSwitchTitle()}
        <div className={styles.properties} ref={this.getContainerRef}>
          {this.renderProperties()}
        </div>
        {this.renderMoreButton()}
      </div>
    );
  }
}

CompanyProperties.propTypes = {
  translate: PropTypes.func,
  locale: PropTypes.string,
  isRtl: PropTypes.bool,
  isMobile: PropTypes.bool,
  forSale: PropTypes.array,
  forRent: PropTypes.array,
  openDetails: PropTypes.func,
  companyName: PropTypes.string,
  toggleFavorite: PropTypes.func,
  favouritePropertiesIDs: PropTypes.array,
  overrideCurrency: PropTypes.string
};

const mapStateToProps = state => ({
  overrideCurrency: state.currency.currency
});

export default connect(mapStateToProps)(CompanyProperties);
